import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { BaseService } from "src/app/@core/services/base.service";
import { IAttachment, IOrganization, IUser, IWorkspace, ResponseJson } from "src/app/@core/models/model";
import { environment } from "src/environments/environment.dev";

export enum CLAUSE_SOURCE {
    MANUAL = "MANUAL",
    EXCEL = "EXCEL",
    EXTRACTED = "EXTRACTED",
}

export enum CLAUSE_STATUS {
    APPROVED = "APPROVED",
    REJECTED = "REJECTED",
    PENDING = "PENDING",
}

export interface IClause extends Document {
    _id: string;
    name: string;
    text: string;
    organization: string | IOrganization;
    workspace: string | IWorkspace;
    clauseBundles: (string | IClauseBundle)[];
    duplicateClausesClusterId: string | IDuplicateClausesCluster;
    duplicateCheckStatus: DUPLICATE_CLAUSES_CHECK_STATUS;
    markedNotDuplicateClausesClusterIds: (string | IDuplicateClausesCluster)[];
    source: CLAUSE_SOURCE;
    status: CLAUSE_STATUS;
    confidence?: number;
    createdBy: string | IUser;
    usageCount: number;
    createdAt?: Date;
    updatedAt?: Date;
}

export interface IClauseBundle extends Document {
    _id: string;
    name: string;
    workspace: string | IWorkspace;
    clauses: (string | IClause)[];
    createdAt?: Date;
    updatedAt?: Date;
}


export enum ClauseSortBy {
    Name = 'name',
    CreatedAt = 'createdAt',
    UsageCount = 'usageCount'
}
export type ClauseSortOrder = 1 | -1;

export interface IClauseFilters {
    searchValue?: string;
    status?: CLAUSE_STATUS;
    skip?: number;
    limit?: number;
    source?: CLAUSE_SOURCE[];
    createdBy?: string[];
    clauseBundle?: string[];
    createdAt?: {
        startDate?: Date;
        endDate?: Date;
    };
    minUsageCount?: number;
    maxUsageCount?: number;
    sortBy?: ClauseSortBy;
    sortOrder?: ClauseSortOrder;
}


export enum CLAUSE_METADATA_STATUS {
    PENDING = 'PENDING',
    IN_PROGRESS = 'IN_PROGRESS',
    COMPLETED = 'COMPLETED',
    FAILED = 'FAILED',
}

export enum CLAUSE_CONFIDENCE {
    HIGH = 'HIGH',
    MEDIUM = 'MEDIUM',
    LOW = 'LOW'
}

export enum DUPLICATE_CLAUSES_CHECK_STATUS {
    PROCESSED = "PROCESSED",
    UNPROCESSED = "UNPROCESSED",
}

export interface IClauseMetadata extends Document {
    _id: string;
    organization: string | IOrganization;
    workspace: string | IWorkspace;
    upload: {
        attachment?: string | IAttachment;
        status: CLAUSE_METADATA_STATUS;
    };
    confidence?: CLAUSE_CONFIDENCE;
    createdBy: string | IUser;
    createdAt: Date;
    updatedAt: Date;
}

export interface IDuplicateClausesCluster extends Document {
    _id: string;
    organization: string | IOrganization;
    workspace: string | IWorkspace;
    clauses: (string | IClause)[];
    clauseBundle: string | IClauseBundle;
}

/** Audit item returned by uploaded-files-audit API (attachment and status at root). */
export interface IClauseMetadataAuditItem {
    _id: string;
    attachment?: IAttachment;
    status: CLAUSE_METADATA_STATUS;
}

@Injectable({ providedIn: 'root' })
export class ClauseLibraryService extends BaseService {

    constructor(
        protected override http: HttpClient
    ) {
        super(http);
    }

    apiUrl = environment.apiUrl + '/clause-library';


    getClauses(filters: IClauseFilters) {
        return this.post<{ clauses: IClause[], total: number }>(this.apiUrl + '/', { filters });
    }

    getClause(clauseId: string) {
        return this.get<IClause>(this.apiUrl + '/' + clauseId);
    }

    createClause(data: Partial<IClause>) {
        return this.post<IClause>(this.apiUrl + '/create', data);
    }

    updateClause(clauseId: string, data: Partial<IClause>) {
        return this.put<IClause>(this.apiUrl + '/' + clauseId, data);
    }


    bulkDeleteClauses(clauseIds: string[]) {
        return this.delete<{ deletedCount: number }>(this.apiUrl + '/', { clauseIds });
    }

    bulkDownloadClauses(clauseIds: string[]) {
        return this.post<any>(this.apiUrl + '/bulk-download', { clauseIds });
    }


    getDuplicateClauses(bundleId?: string) {
        return this.post<{ clusters: IDuplicateClausesCluster[] }>(this.apiUrl + '/duplicate-clauses', { bundleId: bundleId ?? '' });
    }

    updateClausesAsNotDuplicate(clusterId: string, clauseIds: string[]) {
        return this.post<any>(this.apiUrl + '/mark-not-duplicate', { clusterId, clauseIds });
    }

    acceptClauseInCluster(clusterId: string, clauseId: string) {
        return this.post<any>(this.apiUrl + '/accept-duplicate-clause', { clusterId, clauseId });
    }


    getMergeClauseSuggestion(clauseIds: string[]) {
        return this.post<{ clauseName: string; clauseText: string }>(
            this.apiUrl + '/smart-suggestions',
            { clauseIds }
        );
    }

    createMergedClause(payload: { clusterId: string; name: string; text: string; clauseIds: string[]; accept: boolean }) {
        return this.post<any>(this.apiUrl + '/merge-clauses', payload);
    }

    bulkLinkClausesToBundles(clauseIds: string[], bundleIds: string[]) {
        return this.post<any>(this.apiUrl + '/bulk-link-to-bundles', { clauseIds, bundleIds });
    }

    bulkUpdateClauses(updates: Partial<IClause>[]) {
        return this.post<any>(this.apiUrl + '/bulk-update', { updates });
    }

    downloadSampleExcel() {
        return this.get<{ data: Buffer }>(this.apiUrl + '/sample-excel');
    }

    uploadExcelClauses(formData: FormData) {
        return this.post<any>(this.apiUrl + '/upload-excel-clauses', formData);
    }

    submitClauseExtraction(formData: FormData) {
        return this.post<{ metadataIds: string[]; count: number }>(this.apiUrl + '/submit-extraction-request', formData);
    }

    retryClauseExtraction(clauseMetadataId: string, bundleIds: string[] = []) {
        return this.post<{ success: boolean; code?: 'ALREADY_SUCCESS' | 'ALREADY_RUNNING' }>(
            this.apiUrl + '/retry-extraction',
            { clauseMetadataId, bundleIds }
        );
    }

    generateClauseText(clauseName: string, prompt: string) {
        return this.post<{ clauseText: string }>(this.apiUrl + '/generate-clause-text', { clauseName, prompt });
    }

    rephraseClause(clauseName: string, clauseText: string) {
        return this.post<{ clauseText: string }>(this.apiUrl + '/rephrase-clause', { clauseName, clauseText });
    }

    getUploadedFilesAudit(payload: { limit: number; skip: number }) {
        return this.post<IClauseMetadataAuditItem[]>(this.apiUrl + '/uploaded-files-audit', payload);
    }


    deleteClausesFromCluster(clusterId: string, clauseIds: string[]) {
        return this.post<any>(this.apiUrl + '/delete-duplicate-clauses', { clusterId, clauseIds });
    }
}
